import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth'; // <-- Import Auth function
import { db, auth } from '../firebase'; // <-- Import auth
import './UserForm.css';

const UserFormPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    prefix: '',
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    // Add password fields to state
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const navigate = useNavigate();
  const { userId } = useParams();

  const isEditMode = Boolean(userId);

  useEffect(() => {
    if (isEditMode) {
      // For editing, we don't need to show password fields by default
      setShowPasswordFields(false);
      const fetchUser = async () => {
        setLoading(true);
        const userDoc = await getDoc(doc(db,"users grocery", userId));
        if (userDoc.exists()) {
          setFormData(prev => ({ ...prev, ...userDoc.data() }));
        } else {
          alert("User not found.");
          navigate('/users');
        }
        setLoading(false);
      };
      fetchUser();
    } else {
      // For adding, always show password fields
      setShowPasswordFields(true);
    }
  }, [userId, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Password Validation ---
    if (!isEditMode || (isEditMode && formData.password)) {
      if (formData.password.length < 6) {
        alert("Password must be at least 6 characters long.");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        alert("Passwords do not match.");
        return;
      }
    }

    setLoading(true);

    try {
      if (isEditMode) {
        // --- UPDATE EXISTING USER ---
        const userRef = doc(db,"users grocery", userId);
        const { password, confirmPassword, ...userData } = formData;
        await updateDoc(userRef, userData);
        // Note: Updating another user's password client-side is complex and
        // typically requires admin privileges on a server. For now, we are
        // only updating the Firestore data, not the Auth password.
        alert("User details updated successfully!");
        navigate('/users');
      } else {
        // --- ADD NEW USER ---
        // 1. Create the user in Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const newUser = userCredential.user;

        // 2. Save the rest of the user's data in Firestore
        // We use the UID from Auth as the document ID in Firestore
        const { password, confirmPassword, ...userData } = formData;
        await setDoc(doc(db,"users grocery", newUser.uid), {
            ...userData
        });
        
        alert("User created successfully!");
        navigate('/users');
      }
    } catch (error) {
      // Handle specific auth errors
      if (error.code === 'auth/email-already-in-use') {
        alert("This email address is already in use.");
      } else {
        console.error("Error saving user: ", error);
        alert("Failed to save user. Please check the console for details.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Loading user data...</div>;
  }

  return (
    <div className="user-form-container">
      <h2>{isEditMode ? 'Edit User' : 'Add New User'}</h2>
      <form onSubmit={handleSubmit} className="user-form">
        {/* ... other form groups ... */}
        <div className="form-group">
          <label>Username</label>
          <input type="text" name="username" value={formData.username} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>Prefix</label>
          <input type="text" name="prefix" value={formData.prefix} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>First Name</label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>Role</label>
          <input type="text" name="role" value={formData.role} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={isEditMode} />
           {isEditMode && <small>Email cannot be changed.</small>}
        </div>

        {/* --- Password Fields --- */}
        {isEditMode && !showPasswordFields && (
            <div className="form-actions-left">
                <button type="button" className="change-password-btn" onClick={() => setShowPasswordFields(true)}>
                    Change Password
                </button>
            </div>
        )}

        {showPasswordFields && (
            <>
                <div className="form-group">
                    <label>Password</label>
                    <input type="password" name="password" value={formData.password} onChange={handleChange} required={!isEditMode} placeholder={isEditMode ? "Leave blank to keep current password" : ""}/>
                </div>
                <div className="form-group">
                    <label>Confirm Password</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required={!isEditMode} />
                </div>
            </>
        )}
        
        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
          </button>
          <button type="button" className="cancel-btn" onClick={() => navigate('/users')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserFormPage;